/**
 * FlipbookReader — 3D page-flip PDF reader (react-pageflip + react-pdf).
 *
 * Drop-in replacement for `<PdfReader>` — accepts the same props so the
 * parent (BookReader.jsx) can swap without any other change:
 *   src, initialPage, onPageChange, onLoadSuccess, onLoadError,
 *   readingMode, watermarkText, dataTestId
 *
 * Features
 * --------
 *  • 3D page-flip animation on click, tap edge, keyboard arrows, or
 *    swipe. Powered by react-pageflip (StPageFlip under the hood).
 *  • Desktop (>= 768 px) shows a two-page book spread — cover-style
 *    (page 1 solo, then pairs 2-3, 4-5 …). Mobile shows one page at a
 *    time in portrait.
 *  • Anti-copy: pages are rendered to <canvas> only (no text layer),
 *    right-click / drag / copy / print keys blocked, print-CSS blanks
 *    the page.
 *  • Diagonal "Seven Chapter Books" watermark overlaid on every page
 *    the instant the window loses focus (screenshot deterrent).
 *  • Prev / Next toolbar buttons + page number input, kept centered in
 *    the header for parity with the previous reader.
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState, forwardRef,
} from 'react';
import HTMLFlipBook from 'react-pageflip';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

// Pin worker to the bundled pdfjs-dist version — avoids CDN skew.
// Load the pdf.js worker from the BACKEND API endpoint where we control
// the exact MIME type. Webpack-bundled `.mjs` files in `static/media/`
// go through the K8s ingress / static host, which frequently serves them
// as `application/octet-stream` or `text/plain` — browsers strictly
// refuse to execute ES modules with the wrong MIME type, surfacing as
// pdf.js's cryptic "Setting up fake worker failed: Failed to fetch
// dynamically imported module" error on production.
//
// The backend (see `backend/server.py`) explicitly registers `.mjs` as
// `application/javascript` via `mimetypes.add_type(...)` at startup, so
// files served from `/api/static/*.mjs` are always executable ESM. The
// worker file itself lives at `backend/static/pdf-worker.mjs` (copied
// there from `pdfjs-dist/build/pdf.worker.min.mjs` and version-locked
// with the pdfjs-dist dependency in package.json).
//
// Falls back to same-origin if REACT_APP_BACKEND_URL isn't configured
// (dev / preview) — same behaviour as the rest of the app.
const _pdfWorkerBase = process.env.REACT_APP_BACKEND_URL || '';
pdfjs.GlobalWorkerOptions.workerSrc = `${_pdfWorkerBase}/api/static/pdf-worker.mjs`;

// A single page inside the flipbook. Must be a class-like component
// (forwardRef) — react-pageflip introspects children to attach flip
// handlers, so it needs a real DOM node it can grab.
//
// Inner-edge shadows: both left AND right edges are darkened via an
// inset box-shadow. When two pages sit side-by-side the two inner edges
// combine to form a natural-looking spine gutter; the outer edges add a
// subtle "cupped" look against the dark reader chrome, matching how the
// paper of a real open book curves inward toward the binding.
const FlipbookPage = forwardRef(function FlipbookPage(
  { children, className = '', bgClass, watermarkBgUrl, showWatermark },
  ref,
) {
  return (
    <div
      ref={ref}
      className={`relative overflow-hidden ${bgClass} ${className}`}
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        overflow: 'hidden',
        boxSizing: 'border-box',
        // Outer drop-shadow + inner spine/gutter shadows on both edges.
        // The inset shadows are what give the "curved paper" impression.
        boxShadow: [
          '0 4px 24px rgba(0,0,0,0.35)',
          'inset 12px 0 18px -10px rgba(0,0,0,0.55)',
          'inset -12px 0 18px -10px rgba(0,0,0,0.55)',
        ].join(', '),
      }}
    >
      {children}
      {watermarkBgUrl && (
        <div
          aria-hidden="true"
          data-testid="flipbook-watermark"
          className="absolute inset-0 pointer-events-none transition-opacity duration-75"
          style={{
            backgroundImage: watermarkBgUrl,
            backgroundRepeat: 'repeat',
            opacity: showWatermark ? 1 : 0,
          }}
        />
      )}
    </div>
  );
});

const FlipbookReader = ({
  src,
  // Image mode: { count, aspect, urlFor(pageNumber) } — when provided the
  // flipbook shows pre-rendered WebP page images instead of decoding the
  // PDF in the browser (no pdf.js in the reading path at all).
  imagePages = null,
  initialPage = 1,
  onPageChange,
  onLoadSuccess,
  onLoadError,
  readingMode = 'dark',
  watermarkText = null,
  dataTestId = 'flipbook-reader',
}) => {
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialPage);
  // Aspect ratio (height / width) of the source PDF. Seeded to 1.55 and
  // replaced with the exact ratio (probed via pdf.getPage(1) in
  // handleDocumentLoad) BEFORE the flipbook mounts, so the leaf size and
  // the rendered canvas size always agree.
  const [pageAspect, setPageAspect] = useState(1.55);
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1280,
    h: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));
  const [pdfReady, setPdfReady] = useState(false);
  const [showWatermark, setShowWatermark] = useState(false);

  const isMobile = viewport.w < 768;
  const imageMode = !!(imagePages && imagePages.count > 0);

  // Image mode is ready immediately — count and aspect come from the
  // server-side manifest, no PDF probing needed.
  useEffect(() => {
    if (!imageMode) return;
    aspectSnappedRef.current = true;
    const a = Number(imagePages.aspect);
    if (a > 0) setPageAspect(Math.min(2.2, Math.max(0.5, a)));
    setNumPages(imagePages.count);
    setPdfReady(true);
    if (onLoadSuccess) onLoadSuccess({ numPages: imagePages.count });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageMode, imagePages && imagePages.count]);

  const containerRef = useRef(null);
  const flipBookRef = useRef(null);

  // pdf.js document loading options. MUST be memoized to a stable object
  // reference — passing a fresh `{}` on every render causes react-pdf to
  // re-load the whole PDF on each parent update. Configuring:
  //   • disableAutoFetch: true  — pdf.js only fetches the byte ranges it
  //     needs to render the visible page(s), never eagerly reading the
  //     entire file.
  //   • disableStream: true     — pdf.js NEVER consumes a streaming full
  //     body. Every request is a small HTTP Range request. This is
  //     critical for large full-colour books (30-50 MB): the browser
  //     never has to hold a multi-MB response open, and the ingress
  //     never has to buffer/forward the full body — so even a K8s
  //     ingress with a 30-60 s response timeout has no chance of
  //     failing the initial load. Requires the backend to serve Range
  //     requests, which /api/db-files does (see server.py chunked +
  //     non-chunked handlers).
  //   • rangeChunkSize kept at default (65536 = 64 KB) so trailer/xref
  //     probes are cheap.
  const pdfOptions = useMemo(
    () => ({
      disableAutoFetch: true,
      disableStream: true,
      cMapUrl: `${_pdfWorkerBase}/api/static/pdfjs/cmaps/`,
      cMapPacked: true,
      standardFontDataUrl: `${_pdfWorkerBase}/api/static/pdfjs/standard_fonts/`,
    }),
    [],
  );

  // ── Responsive page size — DERIVED synchronously from viewport +
  // aspect so the flipbook never mounts with a stale size. page-flip
  // freezes width/height at construction time (`new PageFlip(el, props)`),
  // so any post-mount size change MUST remount the flipbook — handled by
  // the `key` on <HTMLFlipBook> below.
  const pageSize = useMemo(() => {
    const { w, h } = viewport;
    if (isMobile) {
      const pw = Math.min(w - 24, 500);
      const ph = Math.min(h - 140, Math.round(pw * pageAspect));
      return { w: pw, h: ph };
    }
    const availableW = Math.min(w - 80, 1400);
    const maxH = h - 140;
    let pw = Math.floor(availableW / 2);
    let ph = Math.round(pw * pageAspect);
    if (ph > maxH) {
      ph = maxH;
      pw = Math.floor(ph / pageAspect);
    }
    return { w: pw, h: ph };
  }, [viewport, isMobile, pageAspect]);

  useEffect(() => {
    const onResize = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Watermark: reveal on window blur / tab hide (screenshot signal) ─
  useEffect(() => {
    if (!watermarkText) return undefined;
    const reveal = () => setShowWatermark(true);
    const hide = () => setShowWatermark(false);
    const onVis = () => (document.hidden ? reveal() : hide());
    window.addEventListener('blur', reveal);
    window.addEventListener('focus', hide);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('blur', reveal);
      window.removeEventListener('focus', hide);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [watermarkText]);

  // ── Anti-copy: block right-click, drag, keyboard shortcuts, print ──
  const blockEvt = useCallback((e) => { e.preventDefault(); return false; }, []);
  const blockKeys = useCallback((e) => {
    const mod = e.ctrlKey || e.metaKey;
    const k = (e.key || '').toLowerCase();
    if (mod && ['c', 'x', 'a', 's', 'p', 'u'].includes(k)) {
      e.preventDefault(); e.stopPropagation();
    }
    if (k === 'f12' || (e.shiftKey && mod && (k === 'i' || k === 'j' || k === 'c'))) {
      e.preventDefault(); e.stopPropagation();
    }
  }, []);
  useEffect(() => {
    document.addEventListener('keydown', blockKeys, { capture: true });
    return () => document.removeEventListener('keydown', blockKeys, { capture: true });
  }, [blockKeys]);
  useEffect(() => {
    const styleId = 'flipbook-print-block';
    if (document.getElementById(styleId)) return undefined;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @media print {
        body * { visibility: hidden !important; }
        body::before {
          content: 'Printing is disabled for protected content.';
          visibility: visible !important;
          display: block; padding: 40px; font-size: 18px;
        }
      }
      /* react-pdf's Page div collapses to 0x0 inside the flipbook leaf
         (its canvas ends up absolutely positioned, so the wrapper has no
         in-flow content and flex-centering anchors the canvas at the
         leaf's midpoint — pushing the artwork out of the visible leaf).
         Force the Page to fill the leaf and statically center its canvas. */
      .pdf-flipbook .react-pdf__Page {
        position: static !important;
        width: 100% !important;
        height: 100% !important;
        min-width: 0 !important;
        min-height: 0 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        background-color: transparent !important;
        overflow: hidden !important;
      }
      .pdf-flipbook .react-pdf__Page__canvas {
        position: static !important;
        margin: 0 !important;
        max-width: 100% !important;
        max-height: 100% !important;
        object-fit: contain;
      }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  // ── Watermark SVG (identical to PdfReader's) ──────────────────────
  const watermarkBgUrl = useMemo(() => {
    if (!watermarkText) return null;
    const text = String(watermarkText).slice(0, 60);
    const xmlSafe = (s) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='280' height='200'>
      <g font-family='ui-sans-serif,system-ui,Helvetica,Arial' font-size='18' font-weight='700' transform='rotate(-28 140 100)' fill='rgba(30,30,30,0.22)' stroke='rgba(255,255,255,0.28)' stroke-width='0.6' paint-order='stroke fill' text-anchor='middle'>
        <text x='140' y='60'>${xmlSafe(text)}</text>
        <text x='140' y='160'>${xmlSafe(text)}</text>
      </g>
    </svg>`;
    return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
  }, [watermarkText]);

  const bgClass = useMemo(() => {
    if (readingMode === 'light') return 'bg-white';
    if (readingMode === 'sepia') return 'bg-amber-50';
    return 'bg-gray-900';
  }, [readingMode]);

  // ── PDF Document callbacks ────────────────────────────────────────
  const aspectSnappedRef = useRef(false);
  // Probe page 1's true aspect ratio BEFORE marking the doc ready, so
  // the flipbook mounts exactly once with the correct leaf size.
  const handleDocumentLoad = useCallback(
    async (pdf) => {
      const total = pdf.numPages;
      try {
        const p1 = await pdf.getPage(1);
        const vp = p1.getViewport({ scale: 1 });
        if (vp && vp.width > 0 && vp.height > 0) {
          const clamped = Math.min(2.2, Math.max(0.5, vp.height / vp.width));
          aspectSnappedRef.current = true;
          setPageAspect(clamped);
        }
      } catch { /* keep default aspect */ }
      setNumPages(total);
      setPdfReady(true);
      if (onLoadSuccess) onLoadSuccess({ numPages: total });
    },
    [onLoadSuccess],
  );

  const handleDocumentError = useCallback(
    (err) => {
      setPdfReady(false);
      if (onLoadError) onLoadError(err);
    },
    [onLoadError],
  );

  // Fallback aspect probe — normally handleDocumentLoad already snapped
  // the ratio before mount; this only fires if that probe failed. A
  // late change is safe: pageSize changes → flipbook key changes →
  // clean remount at the right size (preserving the current page).
  const handleFirstPageLoad = useCallback((pdfPage) => {
    if (aspectSnappedRef.current) return;
    try {
      const vp = pdfPage.getViewport({ scale: 1 });
      if (vp && vp.width > 0 && vp.height > 0) {
        const ratio = vp.height / vp.width;
        const clamped = Math.min(2.2, Math.max(0.5, ratio));
        setPageAspect(clamped);
        aspectSnappedRef.current = true;
      }
    } catch { /* silent — keep the default aspect */ }
  }, []);

  // ── Navigation helpers ────────────────────────────────────────────
  // react-pageflip indexes internally from 0. `flipNext`/`flipPrev`
  // move by one leaf (which is one page in single mode, or one spread
  // in double mode — the library handles that transparently).
  const flipPrev = useCallback(() => {
    if (flipBookRef.current && flipBookRef.current.pageFlip) {
      const inst = flipBookRef.current.pageFlip();
      if (inst && typeof inst.flipPrev === 'function') inst.flipPrev();
    }
  }, []);
  const flipNext = useCallback(() => {
    if (flipBookRef.current && flipBookRef.current.pageFlip) {
      const inst = flipBookRef.current.pageFlip();
      if (inst && typeof inst.flipNext === 'function') inst.flipNext();
    }
  }, []);
  const jumpTo = useCallback(
    (pageNumber) => {
      if (!flipBookRef.current || !flipBookRef.current.pageFlip) return;
      const target = Math.max(1, Math.min(numPages || pageNumber, parseInt(pageNumber, 10) || 1));
      // Library expects 0-based page index
      flipBookRef.current.pageFlip().turnToPage(target - 1);
    },
    [numPages],
  );

  // ── Jump to initialPage when parent asks for it (Resume / Bookmarks) ─
  const initialPageAppliedRef = useRef(null);
  useEffect(() => {
    if (!pdfReady || !initialPage || initialPage < 1) return;
    // Only apply each new initialPage once — parent may re-emit the
    // same value across re-renders.
    if (initialPageAppliedRef.current === initialPage) return;
    initialPageAppliedRef.current = initialPage;
    // Slight delay so the flipbook has mounted its internal DOM.
    const t = setTimeout(() => jumpTo(initialPage), 60);
    return () => clearTimeout(t);
  }, [pdfReady, initialPage, jumpTo]);

  // react-pageflip fires `onFlip` with {data: pageIndex0}.
  const handleFlip = useCallback(
    (e) => {
      const idx0 = e && typeof e.data === 'number' ? e.data : 0;
      const pageNumber = idx0 + 1;
      setCurrentPage(pageNumber);
      if (onPageChange) onPageChange(pageNumber, numPages);
    },
    [numPages, onPageChange],
  );

  // Build the pages array only once numPages is known so react-pdf
  // doesn't attempt to render before the doc is loaded.
  const pageIndexes = useMemo(() => {
    if (!numPages) return [];
    return Array.from({ length: numPages }, (_, i) => i + 1);
  }, [numPages]);

  // The flipbook itself — shared by both modes. In image mode it renders
  // <img> leaves (pre-rendered WebP pages from the server); in PDF mode
  // it renders react-pdf <Page> canvases and is wrapped in <Document>.
  const flipbookBlock = pdfReady && numPages > 0 && (
    <div
      className="relative"
      style={{
        width: isMobile ? pageSize.w : pageSize.w * 2,
        maxWidth: '100%',
      }}
    >
      <HTMLFlipBook
        ref={flipBookRef}
        key={`fb-${pageSize.w}x${pageSize.h}-${isMobile ? 'p' : 'l'}`}
        width={pageSize.w}
        height={pageSize.h}
        size="fixed"
        minWidth={280}
        maxWidth={900}
        minHeight={400}
        maxHeight={1400}
        maxShadowOpacity={0.5}
        showCover
        usePortrait={isMobile}
        mobileScrollSupport
        swipeDistance={30}
        drawShadow
        flippingTime={700}
        startPage={Math.max(0, currentPage - 1)}
        className="pdf-flipbook"
        style={{ margin: '0 auto' }}
        onFlip={handleFlip}
        onInit={() => {
          // Emit the mount page so BookReader records the event
          // (also fires after size-change remounts, preserving page)
          if (onPageChange) onPageChange(currentPage, numPages);
        }}
      >
        {pageIndexes.map((pn) => (
          <FlipbookPage
            key={pn}
            bgClass={bgClass}
            watermarkBgUrl={watermarkBgUrl}
            showWatermark={showWatermark}
          >
            {/* Wrapper enforces the exact leaf size and clips overflow.
                Only pages within a small window around the current page
                mount real content — eagerly mounting all pages of a
                239-320 page book allocates hundreds of canvases/images
                and crashes mobile browsers. Far pages render a plain
                paper-white placeholder swapped in as the reader nears. */}
            <div
              style={{
                width: pageSize.w,
                height: pageSize.h,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
                background: '#fff',
              }}
            >
              {Math.abs(pn - currentPage) <= 6 || pn === 1 ? (
                imageMode ? (
                  <img
                    src={imagePages.urlFor(pn)}
                    alt={`Page ${pn}`}
                    draggable={false}
                    loading="eager"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      userSelect: 'none',
                      pointerEvents: 'none',
                    }}
                    data-testid={`flipbook-image-page-${pn}`}
                  />
                ) : (
                  <Page
                    pageNumber={pn}
                    width={pageSize.w}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    onLoadSuccess={handleFirstPageLoad}
                    loading={
                      <div
                        className="w-full flex items-center justify-center"
                        style={{ height: pageSize.h }}
                      >
                        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                      </div>
                    }
                  />
                )
              ) : (
                <div
                  style={{ width: pageSize.w, height: pageSize.h, background: '#fff' }}
                  data-testid={`flipbook-placeholder-${pn}`}
                />
              )}
            </div>
          </FlipbookPage>
        ))}
      </HTMLFlipBook>

      {/* Center spine band — desktop only. Simulates the gutter shadow
          of a physical book. Pointer-events off so it never blocks flip
          gestures. Hidden on the cover and on mobile. */}
      {!isMobile && currentPage > 1 && (
        <div
          aria-hidden="true"
          data-testid="flipbook-spine"
          className="pointer-events-none absolute top-0 bottom-0"
          style={{
            left: '50%',
            transform: 'translateX(-50%)',
            width: '56px',
            background:
              'linear-gradient(90deg, ' +
              'rgba(0,0,0,0) 0%, ' +
              'rgba(0,0,0,0.02) 20%, ' +
              'rgba(0,0,0,0.06) 35%, ' +
              'rgba(0,0,0,0.14) 45%, ' +
              'rgba(0,0,0,0.28) 49.5%, ' +
              'rgba(0,0,0,0.32) 50%, ' +
              'rgba(0,0,0,0.28) 50.5%, ' +
              'rgba(0,0,0,0.14) 55%, ' +
              'rgba(0,0,0,0.06) 65%, ' +
              'rgba(0,0,0,0.02) 80%, ' +
              'rgba(0,0,0,0) 100%)',
            zIndex: 5,
            mixBlendMode: 'multiply',
          }}
        />
      )}
    </div>
  );

  return (
    <div
      ref={containerRef}
      data-testid={dataTestId}
      className={`relative flex flex-col h-full ${bgClass}`}
      onContextMenu={blockEvt}
      onDragStart={blockEvt}
      onCopy={blockEvt}
      onCut={blockEvt}
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
    >
      {/* Toolbar — centred prev/page/next */}
      <div className="grid grid-cols-3 items-center gap-2 px-3 py-2 bg-black/60 backdrop-blur-sm border-b border-white/10 text-white text-sm">
        <div />
        <div className="flex items-center justify-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={flipPrev}
            disabled={currentPage <= 1}
            className="h-7 px-2 text-white hover:bg-white/10"
            data-testid="flipbook-prev-page"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline ml-1">Prev</span>
          </Button>
          <span className="text-xs sm:text-sm tabular-nums whitespace-nowrap">
            Page{' '}
            <input
              type="number"
              min={1}
              max={numPages || 1}
              value={currentPage}
              onChange={(e) => jumpTo(e.target.value)}
              className="inline-block w-14 bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-center text-white"
              data-testid="flipbook-page-input"
            />{' '}
            / <span data-testid="flipbook-total-pages">{numPages || '–'}</span>
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={flipNext}
            disabled={!numPages || currentPage >= numPages}
            className="h-7 px-2 text-white hover:bg-white/10"
            data-testid="flipbook-next-page"
          >
            <span className="hidden sm:inline mr-1">Next</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center justify-end gap-1 text-xs text-gray-400">
          <span className="hidden sm:inline">
            {isMobile ? 'Swipe to turn pages' : 'Click page edges or use arrow keys'}
          </span>
        </div>
      </div>

      {/* Book area */}
      <div className="flex-1 overflow-hidden flex items-center justify-center p-2 sm:p-4">
        {imageMode ? (
          flipbookBlock
        ) : (
          <Document
            file={src}
            options={pdfOptions}
            onLoadSuccess={handleDocumentLoad}
            onLoadError={handleDocumentError}
            loading={
              <div className="text-gray-300 text-sm flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            }
          >
            {flipbookBlock}
          </Document>
        )}
      </div>
    </div>
  );
};

export default FlipbookReader;
